/**
 * @file useSiteAccessMenu.tsx
 * @description Пункт «⋯» конструктора про паузу сайта (phase19.16, SB138;
 * uiux-site-closed.md §2 (а) — ярлык к разделу «Доступ к сайту»): сайт
 * открыт — «Закрыть сайт на время работ…» (то же окно, что в разделе);
 * закрыт магазином — «Снять паузу сайта» в один клик. Пункта
 * нет без права настроек, без состояния (не загрузилось) и при паузе
 * платформы. Состояние только читается: опрос ведёт каркас кабинета.
 *
 * Подпись «Снять паузу сайта», а не «Открыть сайт»: в том же меню есть
 * «Открыть сайт» — витрина в новой вкладке (QA D3). В плашке кнопка
 * остаётся «Открыть сайт» — там смысл ясен из текста рядом.
 */

import { CirclePause, CirclePlay } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { PageAction } from "../ui/page-actions";
import { CloseSiteDialog } from "./CloseSiteDialog";
import { useShopContextKey, useSiteAccess } from "../../hooks/site-access/useSiteAccess";
import { useSiteAccessActions } from "../../hooks/site-access/useSiteAccessActions";
import { activeSiteClosure } from "../../lib/site-access/site-access-view";

/** Подписи пунктов (их ищут тесты и QA). */
export const SITE_CLOSE_MENU_LABEL = "Закрыть сайт на время работ…";
export const SITE_OPEN_MENU_LABEL = "Снять паузу сайта";

export function useSiteAccessMenu(): { items: PageAction[]; dialog: ReactNode } {
  const access = useSiteAccess(useShopContextKey(), "read");
  const actions = useSiteAccessActions(access);
  const [open, setOpen] = useState(false);
  const { state } = access;
  if (!state?.canManage) return { items: [], dialog: null };

  const closure = activeSiteClosure(state, access.serverNow());
  // «Запуск» снимается только с проверкой условий (PL9) — в плашке и разделе «Доступ к сайту».
  if (closure?.source === "platform" || closure?.source === "launch") return { items: [], dialog: null };
  const item: PageAction = closure
    ? { key: "site-open", label: SITE_OPEN_MENU_LABEL, icon: CirclePlay, loading: actions.busy === "open", run: () => void actions.open(closure.id) }
    : { key: "site-close", label: SITE_CLOSE_MENU_LABEL, icon: CirclePause, run: () => setOpen(true) };
  return { items: [item], dialog: <CloseSiteDialog open={open} onClose={() => setOpen(false)} access={access} /> };
}
